'use client'

import { ReactNode, useState } from 'react'

import {
  Accordion,
  Button,
  Container,
  Dialog,
  Divider,
  Icon,
  Image,
  Spacer,
  Text,
} from '@vinted/web-ui'
import {
  ElectronicsBadge24,
  EyeMagnifyingGlass24,
  Money24,
  VerifiedCheck24,
} from '@vinted/monochrome-icons'

import { useImageSrcSet } from '@marketplace-web/shared/assets'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import {
  AccessChannel,
  FaqEntryType,
} from '@marketplace-web/help-center/help-center-faq-entry-url-data'

type AccordionKey = 'verified' | 'secure' | 'time' | 'warranty'

type Props = {
  show: boolean
  onClose: () => void
}

const MandatoryEvsBuyerDialog = ({ show, onClose }: Props) => {
  const translate = useTranslate('item.mandatory_evs_and_warranty.service_dialog.buyer')
  const { src, srcSet } = useImageSrcSet({
    baseName: 'woman-phone-relaxed-350x180',
    darkMode: false,
    assetBasePath: '/assets/electronics-verification',
  })

  const [expandedAccordion, setExpandedAccordion] = useState<AccordionKey | null>(null)

  const helpCenterEvsBuyerUrl = {
    'help-center': (chunks: Array<ReactNode>) => (
      <HelpCenterFaqEntryUrl
        key="help-center"
        type={FaqEntryType.ElectronicsVerificationBuyer}
        accessChannel={AccessChannel.ProductLink}
        render={url => (
          <a href={url} target="_blank" rel="noreferrer">
            {chunks}
          </a>
        )}
      />
    ),
  }

  const helpCenterWarrantyDetailsUrl = {
    'help-center': (chunks: Array<ReactNode>) => (
      <HelpCenterFaqEntryUrl
        key="help-center"
        type={FaqEntryType.WarrantyDetails}
        accessChannel={AccessChannel.ProductLink}
        render={url => (
          <a href={url} target="_blank" rel="noreferrer">
            {chunks}
          </a>
        )}
      />
    ),
  }

  const handleAccordionToggle = (key: AccordionKey) => (isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? key : null)
  }

  return (
    <Dialog
      show={show}
      closeOnOverlay
      defaultCallback={onClose}
      hasScrollableContent
      testId="mandatory-evs-buyer-dialog"
    >
      <div className="u-fill-width">
        <ScrollableArea>
          <Container>
            <Image src={src} srcset={srcSet} scaling="cover" styling="rounded" />
            <Spacer size="large" />
            <Text as="h2" text={translate('heading')} type="heading" />
            <Spacer size="large" />
            <Text as="p" text={translate('body')} type="body" />
            <Spacer size="x2-large" />
            <Text as="h3" text={translate('section_heading')} type="section-heading" />
            <Spacer size="regular" />
            <Accordion
              title={translate('verified_accordion.title')}
              content={
                <Text
                  as="span"
                  text={translate('verified_accordion.body', helpCenterEvsBuyerUrl)}
                  type="body"
                  html
                />
              }
              isExpanded={expandedAccordion === 'verified'}
              onToggle={handleAccordionToggle('verified')}
              closedPrefix={<Icon name={ElectronicsBadge24} />}
              expandedPrefix={<Icon name={ElectronicsBadge24} />}
              headerId="mandatory-evs-buyer-dialog-verified-accordion-header"
              bodyId="mandatory-evs-buyer-dialog-verified-accordion-body"
            />
            <Divider />
            <Accordion
              title={translate('secure_accordion.title')}
              content={
                <Text as="span" text={translate('secure_accordion.body')} type="body" html />
              }
              isExpanded={expandedAccordion === 'secure'}
              onToggle={handleAccordionToggle('secure')}
              closedPrefix={<Icon name={Money24} />}
              expandedPrefix={<Icon name={Money24} />}
              headerId="mandatory-evs-buyer-dialog-secure-accordion-header"
              bodyId="mandatory-evs-buyer-dialog-secure-accordion-body"
            />
            <Divider />
            <Accordion
              title={translate('time_accordion.title')}
              content={<Text as="span" text={translate('time_accordion.body')} type="body" html />}
              isExpanded={expandedAccordion === 'time'}
              onToggle={handleAccordionToggle('time')}
              closedPrefix={<Icon name={EyeMagnifyingGlass24} />}
              expandedPrefix={<Icon name={EyeMagnifyingGlass24} />}
              headerId="mandatory-evs-buyer-dialog-time-accordion-header"
              bodyId="mandatory-evs-buyer-dialog-time-accordion-body"
            />
            <Divider />
            <Accordion
              title={translate('warranty_accordion.title')}
              content={
                <Text
                  as="span"
                  text={translate('warranty_accordion.body', helpCenterWarrantyDetailsUrl)}
                  type="body"
                  html
                />
              }
              isExpanded={expandedAccordion === 'warranty'}
              onToggle={handleAccordionToggle('warranty')}
              closedPrefix={<Icon name={VerifiedCheck24} color="greyscale-level-2" />}
              expandedPrefix={<Icon name={VerifiedCheck24} color="greyscale-level-2" />}
              headerId="mandatory-evs-buyer-dialog-warranty-accordion-header"
              bodyId="mandatory-evs-buyer-dialog-warranty-accordion-body"
            />
            <Divider />
            <Spacer size="regular" />
          </Container>
        </ScrollableArea>
        <Divider />
        <Container>
          <Button
            text={translate('action.close')}
            styling="filled"
            onClick={onClose}
            testId="mandatory-evs-buyer-dialog-close-button"
          />
        </Container>
      </div>
    </Dialog>
  )
}

export default MandatoryEvsBuyerDialog
